import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Alert, LinearProgress, Typography } from '@mui/material';

import { NfcCardTransactionCriteria, emptyNfcCardTransactionCriteria } from 'lib/models/nfcCards';

import useNfcCard from 'hooks/useNfcCard';
import useNfcCardTransactions from 'hooks/useNfcCardTransactions';

import NfcCardHeader from 'components/NfcCardHeader';
import NfcCardTransactionsFilters from 'components/NfcCardTransactionsFilters';
import NfcCardTransactionsTable from 'components/NfcCardTransactionsTable';
import ScreenContainer from 'components/containers/ScreenContainer';

const rowsPerPageOptions = [10, 25, 50];

/** The movements of the balance of one card, narrowed by kind and by days. */
export default function NfcCardTransactionsScreen() {
	const { t } = useTranslation();

	const { cardId = '' } = useParams<{ cardId: string }>();

	const { card, loading } = useNfcCard(cardId);

	const [criteria, setCriteria] = useState<NfcCardTransactionCriteria>(emptyNfcCardTransactionCriteria);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

	const transactions = useNfcCardTransactions(cardId, criteria, page, rowsPerPage);

	const pagination = {
		count: transactions.total,
		page,
		rowsPerPage,
		rowsPerPageOptions,
		onPageChange: (_: unknown, newPage: number) => setPage(newPage),
		onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setRowsPerPage(parseInt(e.target.value, 10));
			setPage(0);
		},
	};

	return (
		<ScreenContainer title={t('nfc:transactions')}>
			<NfcCardHeader card={card} loading={loading} />

			<NfcCardTransactionsFilters
				loading={transactions.loading}
				onSearch={(next) => {
					setCriteria(next);
					setPage(0);
				}}
			/>

			{transactions.failed && (
				<Alert severity="error" sx={{ marginY: 1 }}>
					{t('nfc:transactionsFailed')}
				</Alert>
			)}

			{transactions.loading && <LinearProgress />}

			{!transactions.loading && !transactions.failed && transactions.total === 0 && (
				<Typography variant="body2" sx={{ paddingY: 2 }}>
					{t('nfc:transactionsEmpty')}
				</Typography>
			)}

			<NfcCardTransactionsTable
				cardUsername={card?.username}
				transactions={transactions.transactions}
				pagination={pagination}
			/>
		</ScreenContainer>
	);
}
